import React, { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { Link, useNavigate } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { useEnrollmentCart } from '@/components/enrollment/EnrollmentCartContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { calculateEmployeeTaxes } from '@/components/utils/dynamicTaxCalculations';
import PayStubPreview from '@/components/enrollment/PayStubPreview';
import { 
  ArrowLeft,
  ArrowRight,
  Trash2,
  Shield,
  AlertCircle,
  Heart,
  Receipt
} from 'lucide-react';
import { motion } from 'framer-motion';
import EnrollmentAIChat from '@/components/ai/EnrollmentAIChat';

export default function EnrollmentReview() {
  const { cartItems, removeFromCart, getTotalPremium } = useEnrollmentCart();
  const [employee, setEmployee] = useState(null);
  const [taxAnalysis, setTaxAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const persistStep = async () => {
      if (employee?.id) {
        try {
          await api.entities.Employee.update(employee.id, { last_enrollment_step: 'review' });
        } catch (err) {
          console.error('Failed to persist enrollment review step', err);
        }
      }
    };
    persistStep();
  }, [employee?.id]);

  useEffect(() => {
    if (employee && cartItems.length > 0) {
      calculateTaxImpact();
    }
  }, [employee, cartItems]);

  const loadData = async () => {
    try {
      const user = await api.auth.me();
      const employees = await api.entities.Employee.filter({ user_email: user.email });
      if (employees.length > 0) {
        setEmployee(employees[0]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const calculateTaxImpact = async () => {
    try {
      const totalAnnualPremium = getTotalPremium() * 12;
      const analysis = await calculateEmployeeTaxes(employee, totalAnnualPremium);
      setTaxAnalysis(analysis);
    } catch (err) {
      console.error('Tax calculation failed:', err);
    }
  };

  const getPayPeriodsPerYear = () => {
    const freq = employee?.pay_frequency || 'bi_weekly';
    const periods = {
      weekly: 52,
      bi_weekly: 26,
      semi_monthly: 24,
      monthly: 12
    };
    return periods[freq] || 26;
  };

  const formatCoverageLevel = (level) => {
    return level.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' + ');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center p-6">
        <Card className="max-w-md w-full text-center">
          <CardContent className="p-12 space-y-6">
            <Heart className="w-16 h-16 text-slate-300 mx-auto" />
            <div>
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No Benefits Selected</h3>
              <p className="text-slate-500">Add some benefits to your enrollment to continue.</p>
            </div>
            <Link to={createPageUrl('BenefitsCatalog')}>
              <Button className="bg-emerald-500 hover:bg-emerald-600">
                Browse Benefits
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const periodsPerYear = getPayPeriodsPerYear();
  const totalMonthly = getTotalPremium();
  const perPaycheck = totalMonthly / (periodsPerYear / 12);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 py-12 px-6">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <Link to={createPageUrl('BenefitsCatalog')}>
            <Button variant="ghost">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Benefits
            </Button>
          </Link>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 sm:text-center">Review Your Enrollment</h1>
          <div className="hidden sm:block w-24" />
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Selected Benefits */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Selected Benefits ({cartItems.length})</span>
                  <Badge className="bg-emerald-100 text-emerald-700">
                    ${totalMonthly.toFixed(2)}/month
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {cartItems.map((item) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-xl"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                        <Shield className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{item.product_name}</p>
                        <p className="text-sm text-slate-500">{formatCoverageLevel(item.coverage_level)}</p>
                        {item.coverage_amount > 0 && (
                          <p className="text-xs text-emerald-600 mt-1">
                            Coverage: ${item.coverage_amount.toLocaleString()}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <p className="text-lg font-bold text-slate-900">
                        ${item.monthly_premium.toFixed(2)}<span className="text-sm font-normal text-slate-500">/mo</span>
                      </p>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeFromCart(item.id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </CardContent>
            </Card>

            {/* Enhanced Paycheck Preview */}
            {employee && totalMonthly > 0 && (
              <div className="space-y-6">
                <PayStubPreview employee={employee} monthlyPremium={totalMonthly} />
                
                {/* Additional Tax Savings Card */}
                {taxAnalysis && (
                  <Card className="bg-gradient-to-r from-emerald-500 to-teal-500 border-0">
                    <CardContent className="p-6 text-white text-center">
                      <Receipt className="w-12 h-12 mx-auto mb-3 opacity-90" />
                      <p className="text-sm opacity-90 mb-1">Total Annual Tax Savings</p>
                      <p className="text-5xl font-bold mb-2">${taxAnalysis.savings.total.toFixed(0)}</p>
                      <p className="text-sm opacity-90">
                        That's ${taxAnalysis.savings.monthlySavings.toFixed(2)}/month thanks to pre-tax benefits!
                      </p>
                      <div className="mt-4 pt-4 border-t border-white/20">
                        <p className="text-xs opacity-75">
                          💡 Your benefits are deducted before taxes under Section 125, reducing your taxable income
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

          </div>

          {/* Summary Sidebar */}
          <div className="space-y-6">
            <Card className="sticky top-6">
              <CardHeader>
                <CardTitle className="text-lg">Enrollment Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3 pb-4 border-b">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Total Monthly Premium</span>
                    <span className="font-semibold">${totalMonthly.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Per Paycheck</span>
                    <span className="font-semibold">${perPaycheck.toFixed(2)}</span>
                  </div>
                  {taxAnalysis && (
                    <div className="flex justify-between text-sm">
                      <span className="text-emerald-600">Tax Savings/Year</span>
                      <span className="font-semibold text-emerald-600">+${taxAnalysis.savings.total.toFixed(0)}</span>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <p className="text-sm text-slate-500 font-medium">Benefits Selected</p>
                  {cartItems.map((item) => (
                    <div key={item.id} className="flex items-center justify-between text-sm">
                      <span className="text-slate-700">{item.product_name}</span>
                      <span className="text-slate-500">${item.monthly_premium.toFixed(2)}</span>
                    </div>
                  ))}
                </div>

                <Button
                  onClick={() => navigate(createPageUrl('EnrollmentSign'))}
                  className="w-full h-12 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                >
                  Proceed to Sign
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>

            <Alert>
              <AlertCircle className="w-4 h-4" />
              <AlertDescription className="text-xs">
                You can still go back and modify your selections before signing.
              </AlertDescription>
            </Alert>
          </div>
        </div>
      </div>

      {/* AI Chat Widget */}
      <EnrollmentAIChat context={{ currentPage: 'review', cartItems, employeeData: employee }} />
    </div>
  );
}